<template>
  <div>
    <h4 class="mb-3">{{ $t('contact-for-host') }}</h4>
    <form method="post" @submit.prevent="submitQuery" v-if="!contactsVisible" class="mb-3">

      <p v-if="widget" v-html="widget.widget.description"></p>

      <div class="date-content position-relative">
        <div class="form-group input-icon-box mb-3"
             v-if="widget && widget.content.some(c => c.name === 'dateRange')">
          <label for="detail_contact_date_range" @click="showDatePicker">
            <button class="btn btn-first" type="button">
              <TyIcon icon="ty-calendar" width="1rem"/>
            </button>
          </label>
          <input type="text" name="date_range" id="detail_contact_date_range" class="form-control"
                 :placeholder="widget.content.find(c => c.name === 'dateRange').placeholder" readonly
                 v-model="dateText" @click="showDatePicker">
          <button class="btn btn-times" type="button" @click="reservationCalendarPopup = false">
            x
          </button>
        </div>

        <LazyDateRangePicker
            v-if="reservationCalendarPopup"
            :minDate="(new Date()).setHours(1)"
            :minDuration="1" :alwaysUnder="true"
            :startDate="(arrivalDate && arrivalDate !== leaveDate)  ? datePickerCurrentDates.startDate : null"
            :disabled_dates="reservedDates"
            :date_range="datePickerCurrentDates"
            @onSelect="afterDateRangeSelect"/>

      </div>


      <div class="info-content">

        <div class="form-group input-icon-box mb-3"
             v-if="widget && widget.content.some(c => c.name === 'unit') && !accommodation.reserve_whole_object">
          <label for="detail_contact_date_range" @click="showDatePicker">
            <button class="btn btn-first" type="button">
              <TyIcon icon="ty-house" width="1rem"/>
            </button>
          </label>
          <input type="text" name="date_range" id="detail_contact_date_range" class="form-control"
                 required
                 :placeholder="widget.content.find(c => c.name === 'unit').placeholder" readonly
                 v-model="unitText" @click="showUnitSelectPopup = true">
          <button class="btn btn-times" type="button" @click="showUnitSelectPopup = false">
            x
          </button>
        </div>

        <UnitSelectPopup v-if="showUnitSelectPopup"
                         @selectAction="changeSelectedUnit"
                         :accommodation="accommodation"
                         :selectedUnit="selectedUnit"/>

        <div class="form-group input-icon-box  mb-3"
             v-if="widget && widget.content.some(c => c.name === 'dateRange')">
          <label for="detail_contact_email">
            <button class="btn btn-first" type="button">
              <TyIcon icon="ty-email" width="1rem"/>
            </button>
          </label>
          <input type="email" name="email" id="detail_contact_email" class="form-control" required
                 :placeholder="widget.content.find(c => c.name === 'email').placeholder" v-model="email">
        </div>
        <div class="form-group input-icon-box  mb-3"
             v-if="widget && widget.content.some(c => c.name === 'phone')">
          <label for="detail_contact_phone">
            <button class="btn btn-first" type="button">
              <TyIcon icon="ty-phone" width="1rem"/>
            </button>
          </label>
          <input type="text" name="phone" id="detail_contact_phone" class="form-control"
                 :placeholder="widget.content.find(c => c.name === 'phone').placeholder" v-model="phone" required>
        </div>
        <div class="form-group input-icon-box mb-3"
             v-if="widget && widget.content.some(c => c.name === 'note')">
        <textarea name="text" id="detail_contact_note" class="form-control"
                  @change="storeNote" @keyup="storeNote"
                  :placeholder="widget.content.find(c => c.name === 'note').placeholder"
                  rows="5" v-model="note"></textarea>
        </div>
        <p v-html="$t('privacy-policy-agreement-' + activeCountry.code.toLowerCase())"></p>


        <div class="alert alert-danger mt-4" v-if="tyrionResponseStatus && tyrionResponseStatus.status === 'error'">
          {{ tyrionResponseStatus.msg }}
        </div>

        <button class="btn btn-first btn-rounded w-100 mb-3" type="submit">
          {{ $t('submit-and-show-contact') }}
        </button>

        <p>
          {{ $t('after-contact-submit-button-text') }}
        </p>

      </div>

      <PartsTempPreloader v-if="submitting"/>

    </form>
    <div v-else>

      <p class="mb-4">{{ $t('host-phone-numbers') }}</p>
      <div class="row">
        <div class="col-12 mb-3" v-for="contact in accommodation.contacts">
          <p class="mb-3"><strong>{{ contact.contact_person }}</strong></p>
          <div class="phone" v-for="phone in contact.phones">
            <a :href="`tel:${phone.number}`" class="btn btn-outline-first mb-3 btn-rounded px-4 ">
              <b>{{ formatCellphone(phone.number) }}</b>
            </a>
            <br>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="alert alert-success mt-4"
               v-if="tyrionResponseStatus && tyrionResponseStatus.status === 'success'">
            {{ tyrionResponseStatus.msg }}
          </div>
        </div>
        <div class="col-12">
          <p>{{ $t('thank-you-for-contact') }}</p>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">

import Client from "../../../../../lib/models/Client";
import {computed, onMounted, PropType, ref} from "vue";
import Accommodation from "../../../../../lib/models/Accommodation";
import LazyDateRangePicker from "../../../parts/calendar/calendar_range_picker_double.vue";
import dayjs from "dayjs";
import {useNuxtApp, useRoute} from "#app";
import AccommodationUnit from "../../../../../lib/models/AccommodationUnit";
import UnitSelectPopup from "./right_reservation/acc_detail_right_reservation_unit_select.vue";
import {formatCellphone, formatTime} from "../../../../../lib/functions";
import {GATracker} from "../../../../../composables/ga_tracker";
import {obtainActiveLocale, obtainActiveWebCountry, tyrionResponse} from "#imports";
import Form from "../../../../../lib/models/Form";
import FormElement from "../../../../../lib/models/FormElement";
import {useStateClientData} from "../../../../../composables/state/client_state";
import {useReCaptcha} from 'vue-recaptcha-v3';

const {$i18n, $tyrionApi} = useNuxtApp();
const activeLocale = obtainActiveLocale();
const activeCountry = obtainActiveWebCountry();
const {GATrackEvent} = GATracker();
const {setTyrionResponseStatus, tyrionResponseStatus} = tyrionResponse();
const recaptchaInstance = useReCaptcha();

const props = defineProps({
  client: Object as PropType<Client>,
  accommodation: Object as PropType<Accommodation>
})

const {clientTripData, setContactNote, contactNote} = useStateClientData();
const note = ref<string>("");
const reservationCalendarPopup = ref<boolean>(false);
const email = ref<string>(props.client ? props.client.email : "");
const phone = ref<string>(props.client ? props.client.phone : "");
const selectedUnit = ref<AccommodationUnit>({title: $i18n.t('select'), id: null});
const arrivalDate = ref('2021-01-01');
const leaveDate = ref('2021-01-01');
const loadingClosedDays = ref(false);
const showUnitSelectPopup = ref(false);
const submitting = ref(false);
const contactsVisible = ref(false);
const reservedDates = ref([]);
const errorMessage = ref(null);
const widget = ref<{ form: Form }>();

const dateText = computed(() => {
  return arrivalDate.value !== leaveDate.value ? `${(new Date(arrivalDate.value)).toLocaleDateString()} - ${(new Date(leaveDate.value)).toLocaleDateString()}` : "";
});

const unitText = computed(() => {
  return selectedUnit.value.id > 0 ? selectedUnit.value.title : "";
});

const datePickerCurrentDates = computed(() => {
  return {startDate: dayjs(arrivalDate.value).hour(9), endDate: dayjs(leaveDate.value).hour(9)}
});

const dateRangeDuration = computed(() => {
  if (arrivalDate.value && leaveDate.value) {
    return Number(dayjs(leaveDate.value).diff(dayjs(arrivalDate.value), 'day'));
  }
  return 0;
});

const recaptcha = async (action) => {
  // optional you can await for the reCaptcha load
  await recaptchaInstance?.recaptchaLoaded();
  // get the token, a custom action could be added as argument to the method
  const token = await recaptchaInstance?.executeRecaptcha(action);

  return token;
};

const submitQuery = () => {
  if (submitting.value === false) {

    if (!selectedUnit.value || selectedUnit.value.id < 1) {
      setTyrionResponseStatus('error', $i18n.t('unit-not-selected'));
      return;
    }
    submitting.value = true;


    return $tyrionApi.getTyrionUrl('/api3/csrf/obtain').then(response => {

      recaptcha('contactOwnerPhone').then(token => {

        $tyrionApi.postTyrionUrl('/api3/reservations/contact_host', {
          body: {
            captcha: token,
            form: widget.value.form.id,
            email: email.value,
            csrfToken: response.data,
            phone: phone.value,
            dateRange: {startDate: arrivalDate.value, leaveDate: leaveDate.value},
            accommodation: props.accommodation.id,
            unit: selectedUnit.value.id,
            note: note.value,
            lang: activeLocale.code
          },
          userTokenRequired: true
        }).then((res) => {
          if (res.status === 'success') {
            if(res.message === 'blacklist-email'){
              setTyrionResponseStatus('success', 'form-sent-successfully', true);
            }else {
              setTyrionResponseStatus('success', 'form-sent-successfully', true);
              contactsVisible.value = true;
            }
            submitting.value = false;
          } else {
            setTyrionResponseStatus('error', res.message);
            submitting.value = false;
          }
        }).catch((e) => {
          setTyrionResponseStatus('error', (e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Chat error: ${e.message}`);
          submitting.value = false;
        })

      }).catch(e => {
        setTyrionResponseStatus('error', "Captcha error, Contact provider");
      })


    });
  }
}

const showDatePicker = () => {
  reservationCalendarPopup.value = true;
};

const afterDateRangeSelect = (dateRange) => {
  arrivalDate.value = formatTime(dateRange.startDate, 'YYYY-MM-DD');
  leaveDate.value = formatTime(dateRange.endDate, 'YYYY-MM-DD');
  reservationCalendarPopup.value = false;

  GATrackEvent({
    event: 'reservation_process_date_change',
    language: activeLocale.code,
    action: 'click',
    label: 'Change reservation data in contact modal',
    value: props.accommodation.link,
    items: [
      {
        item_id: props.accommodation.id,
        item_name: props.accommodation.title,
        item_category: props.accommodation.category,
        currency: props.accommodation.main_currency,
        quantity: dateRangeDuration.value,
        date_from: arrivalDate.value,
        item_variant: props.accommodation.location.tourist_region.code
      }
    ]
  }, 700);
};

const changeSelectedUnit = (unit) => {
  selectedUnit.value = unit;
  showUnitSelectPopup.value = false;
};


onMounted(() => {
  setTimeout(() => {
    loadWidgetForm();
    selectDefaultUnit();
    if (clientTripData.value) {
      arrivalDate.value = formatTime(clientTripData.value.from, 'YYYY-MM-DD');
      leaveDate.value = formatTime(clientTripData.value.to, 'YYYY-MM-DD');
    }
    if (contactNote.value) {
      note.value = contactNote.value;
    }
  }, 300);
})

const loadWidgetForm = () => {
  submitting.value = true;
  $tyrionApi.getTyrionUrl(`/api3/widget/form/owner-contact`, {
    query: {langCode: activeLocale.code},
    cacheTime: (2 * 60 * 60)
  }).then(response => {
    const lwidget = response.data;
    lwidget.form = (new Form()).fromObject(lwidget.content.form);
    lwidget.content = Object.keys(lwidget.content.elements).map(key => {
      return (new FormElement()).fromObject(lwidget.content.elements[key]);
    });
    widget.value = lwidget;
    submitting.value = false;
    setContactNote("");
  })
}

const selectDefaultUnit = () => {
  if (props.accommodation.reserve_whole_object && props.accommodation.units && props.accommodation.units.length === 1) {
    const unit = props.accommodation.units[0];
    selectedUnit.value = (new AccommodationUnit()).fromObject(unit)
  } else if (useRoute().query.id_accommodation_unit && props.accommodation.units && props.accommodation.units.some(u => Number(u.id) === Number(useRoute().query.id_accommodation_unit))) {
    selectedUnit.value = (new AccommodationUnit()).fromObject(props.accommodation.units.find(u => Number(u.id) === Number(useRoute().query.id_accommodation_unit)));
  }
  if (selectedUnit.value) {
    loadAvailableDays();
  } else {
    showUnitSelectPopup.value = true;
  }
};
const loadAvailableDays = () => {

  if (loadingClosedDays.value === false && selectedUnit.value.id > 0) {
    errorMessage.value = null;
    reservedDates.value = [];
    loadingClosedDays.value = true;
    $tyrionApi.postTyrionUrl('/api3/accommodation/reserved-days', {
      body: {
        id_accommodation: props.accommodation.id,
        id_unit: [selectedUnit.value.id]
      }
    }).then(res => {
      res.data.forEach(reservation => {
        if(reservation.dates.length > 0) {
          const datesArray = [
            dayjs(reservation.DateFrom),
            ...reservation.dates.map(date => dayjs(date)),
            dayjs(reservation.DateTo)
          ];
          reservedDates.value.push(datesArray);
        }else{
          reservedDates.value.push([dayjs(reservation.DateFrom), dayjs(reservation.DateTo)]);
        }
      })
    }).catch(e => {
      errorMessage.value = (e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Reservation error: ${e.message}`;
    }).finally(() => {
      loadingClosedDays.value = false;
    })

  }
};

const storeNote = () => {
  setContactNote(note.value);
}

</script>
